import { useTranslation } from '@multaqa/i18n'
import { ConfirmDialog } from '@multaqa/ui/components/ui/confirm-dialog'
import type { ReactNode } from 'react'

/**
 * The "are you sure" step in front of an action that posts to the ledger.
 *
 * Posting allocates a gapless document number and writes a journal entry, and
 * neither can be taken back. Deleting a draft — which posts nothing — already
 * asked, and posting an opening-balance batch already asked
 * (`opening-balance-batch-screen.tsx`), so a single click that posted an
 * invoice or a bill was the odd one out: the more consequential action had
 * the less friction.
 *
 * The component carries no domain knowledge: the caller supplies the wording,
 * because only the caller knows which document this is.
 */
export function ConfirmPostDialog({
  open,
  title,
  description,
  confirmLabel,
  pending,
  testId,
  onCancel,
  onConfirm,
  children,
}: {
  open: boolean
  title: string
  description: ReactNode
  confirmLabel: string
  pending: boolean
  testId: string
  onCancel: () => void
  onConfirm: () => void
  /** Block content below the description, e.g. why the last try was refused. */
  children?: ReactNode
}) {
  const { t } = useTranslation('common')

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => (next ? undefined : onCancel())}
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel={t('action.cancel')}
      closeLabel={t('action.close')}
      pending={pending}
      onConfirm={onConfirm}
      testId={testId}
      confirmTestId={`${testId}-accept`}
    >
      {children}
    </ConfirmDialog>
  )
}
